import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Plus, Trash, PencilSimple, Check, X, Gear, Clock, FloppyDisk, UserCircle, WarningCircle, Star } from "@phosphor-icons/react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);

export function AttendancePanel({ year, month }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("records");
  const [records, setRecords] = useState([]);
  const [report, setReport] = useState([]);
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [form, setForm] = useState({ user_id: "", user_name: "", date: new Date().toISOString().slice(0, 10), check_in: "", lunch_out: "", lunch_in: "", check_out: "", notes: "" });

  const filteredReport = roleFilter === "all" ? report : report.filter(r => r.role === roleFilter);

  const selectedMonth = month || new Date().getMonth() + 1;
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [rRes, rpRes, cfgRes, uRes] = await Promise.all([
        axios.get(`${API}/admin/attendance`, { params: { year, month: selectedMonth }, headers }),
        axios.get(`${API}/admin/attendance-report`, { params: { year, month: selectedMonth }, headers }),
        axios.get(`${API}/admin/attendance-config`, { headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
      ]);
      setRecords(rRes.data);
      setReport(rpRes.data);
      setConfig(cfgRes.data);
      setUsers(uRes.data || []);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [year, selectedMonth, token]);

  useEffect(() => { load(); }, [load]);

  const handleUserChange = (uid) => {
    const u = users.find(x => x.id === uid);
    setForm(p => ({ ...p, user_id: uid, user_name: u?.name || "" }));
  };

  const handleSave = async () => {
    if (!form.user_id || !form.date) { toast.error("Selecciona empleado y fecha"); return; }
    try {
      if (editId) {
        await axios.put(`${API}/admin/attendance/${editId}`, form, { headers });
        toast.success("Registro actualizado");
      } else {
        await axios.post(`${API}/admin/attendance`, form, { headers });
        toast.success("Asistencia registrada");
      }
      resetForm(); load();
    } catch { toast.error("Error al guardar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar registro?")) return;
    try { await axios.delete(`${API}/admin/attendance/${id}`, { headers }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({ user_id: r.user_id, user_name: r.user_name, date: r.date, check_in: r.check_in || "", lunch_out: r.lunch_out || "", lunch_in: r.lunch_in || "", check_out: r.check_out || "", notes: r.notes || "" });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditId(null); setShowForm(false);
    setForm({ user_id: "", user_name: "", date: new Date().toISOString().slice(0, 10), check_in: "", lunch_out: "", lunch_in: "", check_out: "", notes: "" });
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/admin/attendance-config`, config, { headers });
      toast.success("Configuracion guardada"); load();
    } catch { toast.error("Error"); }
  };

  const statusColors = { presente: "bg-emerald-100 text-emerald-700", retardo: "bg-amber-100 text-amber-700", falta: "bg-red-100 text-red-700" };
  const statusLabels = { presente: "Presente", retardo: "Retardo", falta: "Falta" };

  const subTabs = [
    { id: "records", label: "Registros" },
    { id: "report", label: "Reporte Mensual" },
    ...(isAdmin ? [{ id: "config", label: "Configuracion" }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="attendance-panel">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* === RECORDS === */}
      {tab === "records" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-500">{MONTHS[selectedMonth - 1]} {year} - {records.length} registros</span>
            <button onClick={() => { resetForm(); setShowForm(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="add-attendance-btn">
              <Plus size={16} /> Registrar Asistencia
            </button>
          </div>

          {showForm && (
            <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="attendance-form">
              <h3 className="font-semibold text-slate-700">{editId ? "Editar Registro" : "Registrar Asistencia"}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Empleado *</label>
                  <select value={form.user_id} onChange={e => handleUserChange(e.target.value)} className="w-full px-2 py-2 border rounded-lg text-sm mt-0.5" data-testid="att-user">
                    <option value="">Seleccionar...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Fecha *</label>
                  <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full px-2 py-2 border rounded-lg text-sm mt-0.5" data-testid="att-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Entrada</label>
                  <input type="time" value={form.check_in} onChange={e => setForm(p => ({ ...p, check_in: e.target.value }))} className="w-full px-2 py-2 border rounded-lg text-sm mt-0.5" data-testid="att-checkin" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Salida comida</label>
                  <input type="time" value={form.lunch_out} onChange={e => setForm(p => ({ ...p, lunch_out: e.target.value }))} className="w-full px-2 py-2 border rounded-lg text-sm mt-0.5" data-testid="att-lunchout" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Entrada comida</label>
                  <input type="time" value={form.lunch_in} onChange={e => setForm(p => ({ ...p, lunch_in: e.target.value }))} className="w-full px-2 py-2 border rounded-lg text-sm mt-0.5" data-testid="att-lunchin" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Salida</label>
                  <input type="time" value={form.check_out} onChange={e => setForm(p => ({ ...p, check_out: e.target.value }))} className="w-full px-2 py-2 border rounded-lg text-sm mt-0.5" data-testid="att-checkout" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Notas</label>
                  <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="w-full px-2 py-2 border rounded-lg text-sm mt-0.5" />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1" data-testid="att-save-btn"><Check size={16} /> {editId ? "Actualizar" : "Guardar"}</button>
                <button onClick={resetForm} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200 flex items-center gap-1"><X size={16} /> Cancelar</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Fecha</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Empleado</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Entrada</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Sal. Comida</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Ent. Comida</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Salida</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Horas</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Extra</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Estado</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Acc.</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {records.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`att-row-${r.id}`}>
                    <td className="px-3 py-2.5 text-slate-600">{r.date}</td>
                    <td className="px-3 py-2.5 font-medium text-slate-700">{r.user_name}</td>
                    <td className="px-3 py-2.5 text-center">{r.check_in || "-"}</td>
                    <td className="px-3 py-2.5 text-center">{r.lunch_out || "-"}</td>
                    <td className="px-3 py-2.5 text-center">{r.lunch_in || "-"}</td>
                    <td className="px-3 py-2.5 text-center">{r.check_out || "-"}</td>
                    <td className="px-3 py-2.5 text-center font-medium">{r.total_hours}h</td>
                    <td className="px-3 py-2.5 text-center">{r.overtime_hours > 0 ? <span className="text-amber-600 font-medium">+{r.overtime_hours}h</span> : "-"}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[r.status] || "bg-slate-100"}`}>
                        {statusLabels[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => startEdit(r)} className="p-1 hover:bg-blue-50 rounded text-blue-600"><PencilSimple size={14} /></button>
                        {isAdmin && <button onClick={() => handleDelete(r.id)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {records.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Sin registros de asistencia</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === MONTHLY REPORT === */}
      {tab === "report" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-semibold text-slate-700">Reporte de Asistencia - {MONTHS[selectedMonth - 1]} {year}</h3>
            <div className="flex gap-1 bg-white border p-0.5 rounded-lg">
              {[{ id: "all", label: "Todos" }, { id: "ingeniero", label: "Ingenieros" }, { id: "comercial", label: "Ventas" }, { id: "admin", label: "Admin" }, { id: "auxiliar", label: "Auxiliar" }].map(f => (
                <button key={f.id} onClick={() => setRoleFilter(f.id)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${roleFilter === f.id ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid={`role-filter-${f.id}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Empleado</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Rol</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Dias</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Faltas</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Retardos</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Horas</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">H.E.</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Asist. Perfecta</th>
                  {isAdmin && <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Bono H.E.</th>}
                  {isAdmin && <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Bono Asist.</th>}
                  {isAdmin && <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Total Bonos</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredReport.map((r, i) => (
                  <tr key={r.user_id} className="hover:bg-slate-50" data-testid={`report-row-${i}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-700 flex items-center gap-2">
                      <UserCircle size={18} className="text-slate-400" /> {r.user_name}
                    </td>
                    <td className="px-3 py-2.5 text-center"><span className="px-2 py-0.5 bg-slate-100 text-xs rounded-full">{r.role || "-"}</span></td>
                    <td className="px-3 py-2.5 text-center font-medium text-emerald-700">{r.days}</td>
                    <td className="px-3 py-2.5 text-center">
                      {r.absences > 0 ? <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">{r.absences}</span> : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {r.lates > 0 ? <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">{r.lates}</span> : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center font-medium">{r.total_hours}h</td>
                    <td className="px-3 py-2.5 text-center">
                      {r.overtime_hours > 0 ? <span className="font-bold text-amber-600">+{r.overtime_hours}h</span> : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {r.perfect_attendance
                        ? <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold flex items-center justify-center gap-1"><Star size={10} weight="fill" /> Si</span>
                        : <span className="text-slate-400">No</span>}
                    </td>
                    {isAdmin && <td className="px-3 py-2.5 text-right font-semibold text-amber-700">{r.overtime_bonus > 0 ? fmt(r.overtime_bonus) : "-"}</td>}
                    {isAdmin && <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{r.perfect_attendance_bonus > 0 ? fmt(r.perfect_attendance_bonus) : "-"}</td>}
                    {isAdmin && <td className="px-3 py-2.5 text-right font-bold text-purple-700">{r.total_bonus > 0 ? fmt(r.total_bonus) : "-"}</td>}
                  </tr>
                ))}
                {filteredReport.length === 0 && <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">Sin datos de asistencia</td></tr>}
              </tbody>
              {isAdmin && filteredReport.length > 0 && (
                <tfoot className="bg-slate-50 border-t-2">
                  <tr className="font-bold">
                    <td className="px-3 py-3" colSpan={5}>TOTALES</td>
                    <td className="px-3 py-3 text-center">{filteredReport.reduce((s, r) => s + r.total_hours, 0).toFixed(1)}h</td>
                    <td className="px-3 py-3 text-center text-amber-600">+{filteredReport.reduce((s, r) => s + r.overtime_hours, 0).toFixed(1)}h</td>
                    <td className="px-3 py-3 text-center text-emerald-700">{filteredReport.filter(r => r.perfect_attendance).length}</td>
                    <td className="px-3 py-3 text-right text-amber-700">{fmt(filteredReport.reduce((s, r) => s + (r.overtime_bonus || 0), 0))}</td>
                    <td className="px-3 py-3 text-right text-emerald-700">{fmt(filteredReport.reduce((s, r) => s + (r.perfect_attendance_bonus || 0), 0))}</td>
                    <td className="px-3 py-3 text-right text-purple-700">{fmt(filteredReport.reduce((s, r) => s + (r.total_bonus || 0), 0))}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* === CONFIG === */}
      {tab === "config" && isAdmin && config && (
        <div className="bg-white rounded-xl border p-6 max-w-xl space-y-4">
          <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Clock size={18} /> Configuracion de Horarios</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Hora Entrada</label>
              <input type="time" value={config.morning_start} onChange={e => setConfig(p => ({ ...p, morning_start: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-morning" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Salida a Comida</label>
              <input type="time" value={config.lunch_start} onChange={e => setConfig(p => ({ ...p, lunch_start: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-lunch-start" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Entrada de Comida</label>
              <input type="time" value={config.lunch_end} onChange={e => setConfig(p => ({ ...p, lunch_end: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-lunch-end" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Hora Salida</label>
              <input type="time" value={config.evening_end} onChange={e => setConfig(p => ({ ...p, evening_end: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-evening" />
            </div>
          </div>
          <hr />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Horas estandar/dia</label>
              <input type="number" value={config.standard_hours} onChange={e => setConfig(p => ({ ...p, standard_hours: parseFloat(e.target.value) || 8 }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-std-hours" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Tolerancia retardo (min)</label>
              <input type="number" value={config.late_tolerance_minutes} onChange={e => setConfig(p => ({ ...p, late_tolerance_minutes: parseInt(e.target.value) || 10 }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-tolerance" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Umbral H.E. para bono (hrs)</label>
              <input type="number" value={config.overtime_bonus_threshold} onChange={e => setConfig(p => ({ ...p, overtime_bonus_threshold: parseFloat(e.target.value) || 8 }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-ot-threshold" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Monto bono por hora extra ($)</label>
              <input type="number" value={config.overtime_bonus_amount} onChange={e => setConfig(p => ({ ...p, overtime_bonus_amount: parseFloat(e.target.value) || 200 }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-ot-amount" />
            </div>
          </div>
          <hr />
          <h4 className="text-sm font-semibold text-slate-600">Bono por Asistencia Perfecta</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Dias requeridos</label>
              <input type="number" value={config.perfect_attendance_days || 30} onChange={e => setConfig(p => ({ ...p, perfect_attendance_days: parseInt(e.target.value) || 30 }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-perfect-days" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Monto bono asistencia perfecta ($)</label>
              <input type="number" value={config.perfect_attendance_bonus || 0} onChange={e => setConfig(p => ({ ...p, perfect_attendance_bonus: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="cfg-perfect-bonus" />
              <p className="text-[10px] text-slate-400 mt-1">0 = desactivado. Se otorga si el empleado cumple los dias sin faltas ni retardos</p>
            </div>
          </div>
          <button onClick={saveConfig} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="save-att-config">
            <FloppyDisk size={16} /> Guardar Configuracion
          </button>
        </div>
      )}
    </div>
  );
}
